import { useState } from "react";

import DataExportGcs from "@assets/data-export-gcs.svg";
import NorthbeamLogo from "@assets/northbeam-error-screen-logo.svg";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type ExportMethodConfig } from "@features/data-export/types";
import { VALIDATE_EXTERNAL_BUCKET_ACCESS } from "@gql/data-export";
import { useNorthbeamMutation } from "@hooks/http";
import {
  DataExportMethod,
  type ValidateExternalBucketAccess,
  type ValidateExternalBucketAccessVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";

import { Notification } from "./notification";

interface Props {
  onExportMethodConfigured(config: ExportMethodConfig): void;
  initialBucketName?: string;
}

export function GCSConfigurator({
  onExportMethodConfigured,
  initialBucketName,
}: Props) {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [validateExternalBucketAccess, { loading }] = useNorthbeamMutation<
    ValidateExternalBucketAccess,
    ValidateExternalBucketAccessVariables
  >(VALIDATE_EXTERNAL_BUCKET_ACCESS);

  const [bucketName, setBucketName] = useState<string>(initialBucketName ?? "");
  const [validationStatus, setValidationStatus] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  return (
    <div className="h-full grid grid-cols-[14rem_auto] overflow-hidden relative">
      <div className="bg-cosmic-4"></div>
      <div className="px-20 py-10 pr-[100px] lg:pr-[200px] xl:pr-[400px] overflow-y-auto">
        <h3 className="text-base font-semibold m-0 mb-6 flex items-center">
          <img
            className="w-[120px] ml-10"
            alt="northbeam-logo"
            src={NorthbeamLogo}
          />
          <i className="fa-regular fa-sharp fa-plus text-[0.7rem] text-gray-500 mx-4"></i>
          <img className="w-[30px]" alt="gcs-logo" src={DataExportGcs} />
        </h3>
        <p className="text-xs text-zinc-500 font-light m-0 mb-10">
          Connect your data export to automatically sync to your Google Cloud
          bucket in two easy steps:{" "}
          <a
            className="font-light text-blue-500"
            href="https://docs.northbeam.io/docs/exporting-data"
            rel="noreferrer"
            target="_blank"
          >
            Access our full instruction guide here
          </a>
        </p>
        <h4 className="text-[0.8rem] font-semibold mb-10">
          Access your Google Cloud bucket and do the following:
        </h4>
        <div className="border-l border-gray-200 pl-6 pb-7 mb-8 ml-[9px] relative">
          <div
            className="absolute left-[-19px] top-[-11px] w-[38px] h-[38px]
                       bg-[#808494] rounded-full border-[10px] border-white p-[1px]"
          >
            <div className="bg-[#f5f8fe] w-full h-full rounded-full"></div>
          </div>
          <h4 className="text-[0.8rem] font-semibold mb-4">
            Grant access to Northbeam
          </h4>
          <p className="text-xs text-zinc-500 font-light m-0 mb-5">
            Add &apos;Storage Object Admin&apos; role to the email listed below:
          </p>
          <p className="text-xs text-zinc-500 font-light underline m-0">
            nb-data-export@north-beam-io.iam.gserviceaccount.com
          </p>
        </div>
        <div className="border-l border-gray-200 pl-6 ml-[9px] relative">
          <div
            className="absolute left-[-19px] top-[-11px] w-[38px] h-[38px]
                       bg-[#808494] rounded-full border-[10px] border-white p-[1px]"
          >
            <div className="bg-[#f5f8fe] w-full h-full rounded-full"></div>
          </div>
          <h4 className="text-[0.8rem] font-semibold mb-4">Add your bucket</h4>
          <p className="text-xs text-zinc-500 font-light m-0 mb-4">
            Add your bucket name you would like to export the data into, then
            click validate.
          </p>
          <div className="mb-8">
            <input
              className="w-[70%] text-xs font-light shadow-none border border-[#D1D5DB]
                       focus:border-[#3B82F6] rounded-md px-3 py-2 outline-0"
              type="text"
              value={bucketName}
              onChange={(ev) => setBucketName(ev.target.value)}
            />
          </div>
          {validationStatus === "success" && (
            <Notification type="success" title="Bucket validated" />
          )}
          {validationStatus === "error" && (
            <Notification
              type="error"
              title="Bucket invalid"
              subtitle="Access Denied. Make sure you added correct permissions to your bucket."
              details={validationErrorMessage}
            />
          )}
          {validationStatus !== "success" && (
            <PrimaryButton
              className="font-light text-xs min-w-[90px] min-h-[34px] pt-1.5 pb-1.5 pl-4 pr-4
                         border border-[#367BF3] flex justify-center items-center mt-5"
              loading={loading}
              isDisabled={!bucketName}
              onClick={async () => {
                prettyLogEventPage({
                  domain: "DataExport",
                  action: "onClick",
                  additionalActionInfo: "Validate external bucket access",
                  data: {
                    pageContext: { bucketName },
                  },
                });

                const result = await validateExternalBucketAccess({
                  variables: { bucketName },
                });

                if (result.data) {
                  const { status, message } =
                    result.data.validateExternalBucketAccess;

                  setValidationStatus(status);
                  setValidationErrorMessage(message);

                  if (status === "success") {
                    onExportMethodConfigured({
                      method: DataExportMethod.EXTERNAL_GCS_BUCKET,
                      details: bucketName,
                    });
                  }
                }
              }}
            >
              VALIDATE
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
}
